import type { Logger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { fetchFeature } from "@asurion-core/expert-workspace-feature-flagging";
import { trackAppcuesEvent } from "@expert/monitoring";
import { useEffect, useMemo, useState } from "react";

import type { Group } from "./types";

import { FLAG_NAME_MAP } from "./defaults";
export interface ContextProps {
    agentSite?: string;
    appName: "expert-assist-standalone" | "expert-workspace";
    employeeId: string;
    environment: string;
    lob?: string;
    partner: Partner;
    source: "expert-assist" | "expert-ui";
}

export interface FeatureFlagProps<T extends Record<string, unknown>> {
    analyticGroup: Group;
    context: ContextProps;
    features: T;
    logger: Logger;
}

export function useFlags<T extends Record<string, unknown>>({
    analyticGroup: group,
    context,
    features,
    logger: loggerProp,
}: FeatureFlagProps<T>) {
    const [updatedFeatures, setUpdatedFeatures] = useState(features);
    const logger = useMemo(() => loggerProp.child({ module: "useFlags" }), [loggerProp]);

    const { dispatcher } = useReactAnalytics();

    useEffect(() => {
        const { employeeId, partner } = context;

        if (!employeeId || !partner) {
            logger.info({ context }, "Feature Flags | missing context for fetching flags");
            return;
        }

        // A context change re-runs this effect while the previous run's requests are still in flight.
        // Drop the superseded run's results so they can't overwrite flags fetched for the newer context.
        let isSuperseded = false;

        const fetchFeatures = async () => {
            const featureKeys = Object.keys(features);

            logger.info({ context, featureKeys }, "Feature Flags | fetching flags");

            const results = await Promise.all(
                featureKeys.map(async (feature) => {
                    // Use mapped flag name if available, otherwise use the feature key as-is
                    const actualFlagName = FLAG_NAME_MAP[feature] ?? feature;
                    return { feature, result: await fetchFeature(actualFlagName, { ...context }) };
                }),
            );

            if (isSuperseded) return;

            const resultsObject: Record<string, unknown> = { ...features };
            const failedKeys: string[] = [];

            results.forEach(({ feature, result }) => {
                if (!result.ok) {
                    // Keep the default either way, but record a failure: it must not read as "the rule said
                    // false". An unconfigured key is a rollout state, not a failure, so it isn't counted.
                    if (result.reason === "failed") failedKeys.push(feature);
                } else if (result.value !== undefined) {
                    resultsObject[feature] = result.value;
                }
            });

            setUpdatedFeatures(resultsObject as T);

            if (failedKeys.length > 0) {
                logger.warn({ context, failedKeys }, "Feature Flags | flags fell back to defaults after fetch failure");
            }
            logger.info({ context, failedKeys, features: resultsObject }, "Feature Flags | fetched flags");

            void dispatcher.withExtra({ partner }).dispatchBusinessEvent("FeatureFlagResults", {
                ...context,
                failedKeys,
                features: resultsObject,
                group,
            });
            trackAppcuesEvent("FeatureFlagResults", { ...context, failedKeys, features: resultsObject, group });
        };

        void fetchFeatures();

        return () => {
            isSuperseded = true;
        };
        // oxlint-disable-next-line exhaustive-deps
    }, [context, features]);

    return updatedFeatures;
}
